import { useUserRoles } from './useUserRoles';
import { useCheckPermission } from './useUserRoles';
import { useAuth } from './useAuth';
import { useAuthQuery } from './useAuthQuery';
import { supabase } from '@/integrations/supabase/client';

/**
 * Check if user can manage project-level permissions
 *
 * Allowed roles (STRICT_PERMISSION_UI hardcoded true — PR-B 2026-04-30):
 * - Admin (via useUserRoles)
 * - Project Owner (creator)
 * - Team Lead (in project_team_leads)
 * - Has ASSIGN_ROLES or MANAGE_ROLES permission (PM/SOL)
 *
 * Legacy `can_edit`-only branch removed; users with can_edit but none of the
 * elevated roles above can no longer manage permissions.
 */
export const useCanManageProjectPermissions = (projectId: string) => {
  const { user } = useAuth();
  const { data: userRoles } = useUserRoles();

  // Check PM/SOL permissions
  const { data: hasAssignRoles } = useCheckPermission('ASSIGN_ROLES');
  const { data: hasManageRoles } = useCheckPermission('MANAGE_ROLES');

  // Check if user is project owner
  const { data: isOwner } = useAuthQuery<boolean>({
    queryKey: ['is-project-owner', projectId, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('projects')
        .select('user_id')
        .eq('id', projectId)
        .single();
      return data?.user_id === user?.id;
    },
    requiresAuth: true,
    enabled: !!projectId && !!user?.id,
  });

  // Check if user is team lead
  const { data: isTeamLead } = useAuthQuery<boolean>({
    queryKey: ['is-team-lead', projectId, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('project_team_leads')
        .select('id')
        .eq('project_id', projectId)
        .eq('user_id', user?.id)
        .maybeSingle();
      return !!data;
    },
    requiresAuth: true,
    enabled: !!projectId && !!user?.id,
  });

  // Admin check
  const isAdmin = userRoles?.some(role => role.role_code === 'ROLE_ADMIN');

  return (
    isAdmin ||
    isOwner ||
    isTeamLead ||
    hasAssignRoles ||
    hasManageRoles
  );
};
